import { useState, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import "./JobSimpleSearchBox.css";
import Button from "../Button/Button";
import TextInput from "../TextInput/TextInput";
import searchFilterIcon from "../../Assets/Images/search-filter-icon.svg";
import Badge from "@mui/material/Badge";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import jellyIconLight from "../../Assets/Images/Jobs Search Icon Light.svg";
import jellyIconDark from "../../Assets/Images/Jobs Search Icon Dark.svg";
import dropRoundLight from "../../Assets/Images/drop-down-round-light.svg";
import dropRoundDark from "../../Assets/Images/drop-down-round-dark.svg";
import { DynamicContext } from "../../Context/DynamicContext";
import DynamicAutocomplete from "../AutoSuggestion/DynamicAutocomplete";
import SelectInput from "../Select-Input/SelectInput";
import OptionsData from "../../Assets/Json/StaticOptions.json";
import SidePopup from "../SidePopup/SidePopup";
import ClientFilterDark from "../../Assets/Images/clients-filter-dark.svg";
import ClientFilterLight from "../../Assets/Images/clients-filter-light.svg";
import CreateByDark from "../../Assets/Images/createdby-filter-dark.svg";
import CreateByLight from "../../Assets/Images/createdby-filter-light.svg";
import ExpDark from "../../Assets/Images/exp-filter-dark.svg";
import ExpLight from "../../Assets/Images/exp-filter-light.svg";
import JobTypeDark from "../../Assets/Images/job-type-dark.svg";
import JobTypeLight from "../../Assets/Images/job-type-light.svg";
import JobRoleDark from "../../Assets/Images/job-role-dark.svg";
import JobRoleLight from "../../Assets/Images/job-role-light.svg";
import LocationDark from "../../Assets/Images/location-filter-dark.svg";
import LocationLight from "../../Assets/Images/location-filter-light.svg";
import PriorityDark from "../../Assets/Images/priority-filter-dark.svg";
import PriorityLight from "../../Assets/Images/priority-filter-light.svg";
import SkillDark from "../../Assets/Images/skills-filter-dark.svg";
import SkillLight from "../../Assets/Images/skills-filter-light.svg";

const { StaticOptions = {} } = OptionsData || {};
const { priority: PRIORITY_OPTIONS = [] } = StaticOptions || {};

const JobSimpleSearchBox = ({
  queryValue = "",
  onQueryChange,
  jobRoleValue = "",
  jobTypeValue = "",
  locationValue = "",
  skillsValue = "",
  clientValue = "",
  clientContactValue = "",
  priorityValue = "",
  minYearsExperienceValue = "",
  maxYearsExperienceValue = "",
  onJobRoleChange,
  onJobTypeChange,
  onLocationChange,
  onSkillsChange,
  onPriorityChange,
  onMinYearsExperienceChange,
  onMaxYearsExperienceChange,
  onClientValueChange,
  onClientContactChange,
  onSubmit,
  queryPlaceholder = "Search for job, e.g., Angular Developer",
  jobRolePlaceholder = "Job Role ( e.g,React Developer )",
  jobTypePlaceholder = "Job Type ( e.g, Full-time, Part-time)",
  locationPlaceholder = "Location ( e.g., New York, USA )",
  skillsPlaceholder = "Skills ( e.g., JavaScript, React,Node Js )",
  minYearsExperiencePlaceholder = "Min Years Exp",
  maxYearsExperiencePlaceholder = "Max Years Exp",
  buttonLabel = "Search",
  recommendedSearchBoxTitle = "Job Search",
  subText = "Find the perfect candidate for your position",
  isCollapsed = false,
  onCollapseChange,
  submitErrorMessage = "Please enter a query to search.",
  onClearForm,
  hasSearched = false,
  isSimpleSearchLoading = false,
  showAdvancedFilters = false,
  clientKey,
  clientOnInputChange,
  clientOnOpen,
  clientNoOptionsText,
  clientOptions,
  clientLoading,
  clientContactOptions = [],
  userAssignedTo = [],
  userJobRelationValue = "",
  onUserJobRelationChange = () => {},
  currentUserId = "",
  advancedFiltersInSidePopup = false,
  publicSearch = false,
}) => {
  const { isDarkMode, isHiringTeamTenant, AdminAccess } =
    useContext(DynamicContext);

  const [collapsed, setCollapsed] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] =
    useState(showAdvancedFilters);
  const [isAdvancedFilterPopupOpen, setIsAdvancedFilterPopupOpen] =
    useState(false);
  const activeFiltersCount = [
    jobRoleValue,
    jobTypeValue,
    locationValue,
    skillsValue,
    minYearsExperienceValue,
    maxYearsExperienceValue,
    !publicSearch && clientValue,
    !publicSearch && clientContactValue,
    priorityValue,
    !publicSearch && userJobRelationValue,
  ].filter((val) => val && String(val).trim() !== "").length;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSimpleSearchLoading) return;
    const hasQuery = queryValue?.trim();
    const hasAnyFilter = activeFiltersCount > 0;

    if (hasQuery || hasAnyFilter) {
      const searchParams = {
        ...(!publicSearch && { active_only: true }),
        limit: 20,
        page: 1,
      };
      if (queryValue?.trim()) {
        searchParams.query = queryValue.trim();
      }
      if (jobRoleValue?.trim()) {
        searchParams.job_role = jobRoleValue.trim();
      }
      if (jobTypeValue?.trim()) {
        searchParams.job_type = jobTypeValue.trim();
      }
      if (locationValue?.trim()) {
        searchParams.location = locationValue.trim();
      }
      if (skillsValue?.trim()) {
        const skillsArray = skillsValue
          .split(",")
          .map((skill) => skill.trim())
          .filter((skill) => skill !== "");

        if (skillsArray.length > 0) {
          searchParams.skills = skillsArray;
        }
      }
      if (minYearsExperienceValue?.toString().trim()) {
        const minExp = parseInt(minYearsExperienceValue, 10);
        if (!isNaN(minExp)) {
          searchParams.min_years_experience = minExp;
        }
      }
      if (maxYearsExperienceValue?.toString().trim()) {
        const maxExp = parseInt(maxYearsExperienceValue, 10);
        if (!isNaN(maxExp)) {
          searchParams.max_years_experience = maxExp;
        }
      }
      if (!publicSearch && clientValue?.value) {
        searchParams.client_id = clientValue.value;
      }
      if (!publicSearch && clientContactValue?.toString().trim()) {
        searchParams.client_contact_id = clientContactValue;
      }
      if (priorityValue?.trim()) {
        const priority = Number(priorityValue);
        if (!Number.isNaN(priority)) {
          searchParams.priority = priority;
        }
      }
      if (!publicSearch && userJobRelationValue?.trim() && currentUserId) {
        searchParams[userJobRelationValue] = currentUserId;
      }

      onSubmit(searchParams);
    } else {
      Toast("error", submitErrorMessage);
    }
  };

  const handleClearSearch = () => {
    if (onClearForm) {
      onClearForm();
    } else {
      if (onQueryChange) {
        onQueryChange({ target: { value: "" } });
      }
      if (onJobRoleChange) {
        onJobRoleChange({ target: { value: "" } });
      }
      if (onJobTypeChange) {
        onJobTypeChange({ target: { value: "" } });
      }
      if (onLocationChange) {
        onLocationChange({ target: { value: "" } });
      }
      if (onSkillsChange) {
        onSkillsChange({ target: { value: "" } });
      }
      if (onMinYearsExperienceChange) {
        onMinYearsExperienceChange({ target: { value: "" } });
      }
      if (onMaxYearsExperienceChange) {
        onMaxYearsExperienceChange({ target: { value: "" } });
      }
      if (onClientValueChange) {
        onClientValueChange(null);
      }
      if (onClientContactChange) {
        onClientContactChange({ target: { value: "" } });
      }
      if (onPriorityChange) {
        onPriorityChange({ target: { value: "" } });
      }
      if (onUserJobRelationChange) {
        onUserJobRelationChange({ target: { value: "" } });
      }
    }
  };
  const toggleCollapse = () => {
    const newCollapsedState = !collapsed;
    setCollapsed(newCollapsedState);
    if (onCollapseChange) {
      onCollapseChange(newCollapsedState);
    }
  };

  const toggleAdvancedSearch = () => {
    if (advancedFiltersInSidePopup) {
      setIsAdvancedFilterPopupOpen(true);
      return;
    }
    setShowAdvancedSearch(!showAdvancedSearch);
  };

  const handleAdvancedFilterSubmit = (e) => {
    handleSubmit(e);
    setIsAdvancedFilterPopupOpen(false);
  };

  const handleMinYearsExperienceChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      onMinYearsExperienceChange(e);
      return;
    }
    if (/^[1-9][0-9]?$|^0$|^[1-9]$/.test(value)) {
      const numValue = parseInt(value, 10);
      if (numValue >= 0 && numValue <= 99) {
        onMinYearsExperienceChange(e);
      }
    }
  };

  const handleMaxYearsExperienceChange = (e) => {
    const value = e.target.value;
    if (value === "") {
      onMaxYearsExperienceChange(e);
      return;
    }
    if (/^[1-9][0-9]?$|^0$|^[1-9]$/.test(value)) {
      const numValue = parseInt(value, 10);
      if (numValue >= 0 && numValue <= 99) {
        onMaxYearsExperienceChange(e);
      }
    }
  };

  useEffect(() => {
    if (isCollapsed !== collapsed) {
      setCollapsed(isCollapsed);
    }
  }, [isCollapsed]);

  const renderAdvancedSearchFields = (isPopup = false) => (
    <div
      className={`job-advanced-search-fields${
        isPopup ? " job-advanced-search-fields-popup" : ""
      }`}
    >
      <div className="job-advanced-field">
        <TextInput
          label={
            <span className="job-advanced-filter-label">
              <img src={isDarkMode ? JobRoleDark : JobRoleLight} alt="" />
              Job Role
            </span>
          }
          value={jobRoleValue}
          onChange={onJobRoleChange}
          placeholder={jobRolePlaceholder}
          style={{
            border: "1px solid var(--search-input-section-border)",
          }}
        />
      </div>

      <div className="job-advanced-field">
        <TextInput
          label={
            <span className="job-advanced-filter-label">
              <img src={isDarkMode ? JobTypeDark : JobTypeLight} alt="" />
              Job Type
            </span>
          }
          value={jobTypeValue}
          onChange={onJobTypeChange}
          placeholder={jobTypePlaceholder}
          style={{
            border: "1px solid var(--search-input-section-border)",
          }}
        />
      </div>

      <div className="job-advanced-field">
        <TextInput
          label={
            <span className="job-advanced-filter-label">
              <img src={isDarkMode ? LocationDark : LocationLight} alt="" />
              Location
            </span>
          }
          value={locationValue}
          onChange={onLocationChange}
          placeholder={locationPlaceholder}
          style={{
            border: "1px solid var(--search-input-section-border)",
          }}
        />
      </div>

      <div className="job-advanced-field">
        <TextInput
          label={
            <span className="job-advanced-filter-label">
              <img src={isDarkMode ? SkillDark : SkillLight} alt="" />
              Skills
            </span>
          }
          value={skillsValue}
          onChange={onSkillsChange}
          placeholder={skillsPlaceholder}
          style={{
            border: "1px solid var(--search-input-section-border)",
          }}
        />
      </div>

      <div className="job-advanced-field">
        <span className="job-advanced-filter-label">
          <img src={isDarkMode ? PriorityDark : PriorityLight} alt="" />
          Priority
        </span>
        <SelectInput
          options={PRIORITY_OPTIONS}
          value={priorityValue}
          onChange={onPriorityChange}
          name="priority"
          defaultOption="Priority"
          defaultOptionStyle={{
            color: "var(--text-secondary)",
            opacity: 0.7,
          }}
          wrapperStyle={{ width: "100%", marginBottom: 0 }}
          selectStyle={{
            fontFamily: "Satoshi",
            fontSize: "14px",
            fontWeight: 500,
            color: "var(--header__color)",
            borderRadius: "8px",
          }}
          selectSx={{
            "& .MuiSelect-select": {
              padding: "10px 12px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid var(--search-input-section-border)",
              borderRadius: "8px",
            },
          }}
        />
      </div>

      <div className="job-advanced-field job-experience-field">
        <TextInput
          label={
            <span className="job-advanced-filter-label">
              <img src={isDarkMode ? ExpDark : ExpLight} alt="" />
              Min Experience
            </span>
          }
          value={minYearsExperienceValue}
          onChange={handleMinYearsExperienceChange}
          placeholder={minYearsExperiencePlaceholder}
          style={{
            border: "1px solid var(--search-input-section-border)",
          }}
        />
        <TextInput
          label={
            <span className="job-advanced-filter-label">
              <img src={isDarkMode ? ExpDark : ExpLight} alt="" />
              Max Experience
            </span>
          }
          value={maxYearsExperienceValue}
          onChange={handleMaxYearsExperienceChange}
          placeholder={maxYearsExperiencePlaceholder}
          style={{
            border: "1px solid var(--search-input-section-border)",
          }}
        />
      </div>

      {!publicSearch && AdminAccess && (
        <div className="job-client-fields-container">
          <div style={{ width: "100%" }}>
            <span className="job-advanced-filter-label">
              <img
                src={isDarkMode ? ClientFilterDark : ClientFilterLight}
                alt=""
              />
              {isHiringTeamTenant ? "Hiring Team" : "Client"}
            </span>
            <DynamicAutocomplete
              onChange={onClientValueChange}
              value={clientValue}
              placeholder={isHiringTeamTenant ? "Hiring Team" : "Client"}
              onInputChange={clientOnInputChange}
              onOpen={clientOnOpen}
              noOptionsText={clientNoOptionsText}
              options={clientOptions}
              loading={clientLoading}
              sx={{
                mt: "5px",
                ".MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  height: "40px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid var(--border__color) !important",
                    borderRadius: "12px",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6860ee",
                    borderRadius: "12px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#7a70f2",
                    borderRadius: "12px",
                  },
                },
                ".MuiInputBase-input": {
                  color: "var(--header__color)",
                  padding: "8px 12px !important",
                  height: "40px",
                  fontFamily: "Satoshi !important",
                },
              }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "var(--body__background)",
                    color: "var(--header__color)",
                    borderRadius: "12px",
                    boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                    border: "1px solid var(--border__color)",
                  },
                },
                listbox: {
                  sx: {
                    "& li": {
                      fontSize: "14px",
                      fontWeight: 500,
                      padding: "8px 16px",
                      color: "var(--header__color)",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    },
                  },
                },
              }}
            />
            {clientValue?.value && !isHiringTeamTenant && (
              <SelectInput
                placeholder="Select"
                options={clientContactOptions}
                name="client_contact_id"
                value={clientContactValue}
                onChange={onClientContactChange}
                label="Client Contact"
                wrapperStyle={{
                  display: "flex",
                  width: "100%",
                  marginTop: "15px",
                  marginBottom: 0,
                }}
                labelStyle={{
                  fontSize: "16px",
                  fontWeight: 600,
                  width: "fit-content",
                  marginBottom: "12px",
                }}
                labelKey="label"
                labelValue="value"
              />
            )}
          </div>
        </div>
      )}

      {!publicSearch && <div className="job-advanced-field">
        <span className="job-advanced-filter-label">
          <img src={isDarkMode ? CreateByDark : CreateByLight} alt="" />
          Created By Me / Assigned To Me
        </span>
        <SelectInput
          options={userAssignedTo}
          value={userJobRelationValue}
          onChange={onUserJobRelationChange}
          name="user_job_relation"
          labelKey="label"
          defaultOption="Created / Assigned"
          defaultOptionStyle={{
            color: "var(--text-secondary)",
            opacity: 0.7,
          }}
          wrapperStyle={{ width: "100%", marginBottom: 0 }}
          selectStyle={{
            fontFamily: "Satoshi",
            fontSize: "14px",
            fontWeight: 500,
            color: "var(--header__color)",
            borderRadius: "8px",
          }}
          selectSx={{
            "& .MuiSelect-select": {
              padding: "10px 12px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid var(--search-input-section-border)",
              borderRadius: "8px",
            },
          }}
        />
      </div>}
    </div>
  );

  return (
    <div className="job-simple-search-container">
      <div
        className={`job-recommended-search-header${collapsed ? " collapsed" : ""}`}
        style={{
          borderBottomLeftRadius: collapsed ? "16px" : "0px",
          borderBottomRightRadius: collapsed ? "16px" : "0px",
        }}
      >
        <img
          src={isDarkMode ? jellyIconDark : jellyIconLight}
          alt="Jelly Icon"
          className="jelly-icon"
        />
        <span className="job-recommended-search-title">
          {recommendedSearchBoxTitle}
        </span>
        <span className="job-recommended-search-subtext">{subText}</span>
        <img
          src={isDarkMode ? dropRoundDark : dropRoundLight}
          style={{
            transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.25s ease",
          }}
          alt="Toggle"
          className={`dropdown-icon toggle-icon ${
            collapsed ? "collapsed" : ""
          }`}
          onClick={toggleCollapse}
        />
      </div>

      {!collapsed && (
        <>
          <div className="job-rec-search-input-section-wrap">
            <div className="job-search-input-with-advanced">
              <div className="job-rec-search-input-section">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    border: "1px solid var(--search-input-section-border)",
                    borderRight: "none",
                    borderRadius: "10px 0px 0px 10px",
                    backgroundColor: "var(--body__background)",
                    paddingLeft: "20px",
                    paddingRight: "8px",
                  }}
                >
                  <input
                    type="text"
                    value={queryValue}
                    onChange={onQueryChange}
                    name="query"
                    placeholder={queryPlaceholder}
                    style={{
                      width: "100%",
                      border: "none",
                      backgroundColor: "transparent",
                      color: "var(--header__color)",
                      outline: "none",
                      fontSize: "14px",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSubmit(e);
                      }
                    }}
                  />
                  {hasSearched && (
                    <div className="simple-inline-actions">
                      <Button
                        label="✕ Clear Search"
                        event={handleClearSearch}
                        className="cephea-clear-search-button"
                      />
                    </div>
                  )}
                </div>
                <Button
                  label={buttonLabel}
                  event={handleSubmit}
                  backgroundColor="var(--search-button-background-color)"
                  color="#ffffff"
                  iconWidth="20px"
                  className="search-button-sharp-left"
                  superShine={true}
                  isLoading={isSimpleSearchLoading}
                  disabled={isSimpleSearchLoading}
                  style={{
                    borderRadius: "0px 10px 10px 0px",
                    backgroundColor: "var(--search-button-background-color)",
                    color: "#ffffff",
                    opacity: isSimpleSearchLoading ? 0.6 : 1,
                    cursor: isSimpleSearchLoading ? "not-allowed" : "text",
                    padding: "12px 14px",
                  }}
                />
                <Button
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontWeight: "500",
                      }}
                    >
                      <Badge
                        badgeContent={activeFiltersCount}
                        color="primary"
                        invisible={activeFiltersCount === 0}
                        sx={{
                          "& .MuiBadge-badge": {
                            fontSize: "10px",
                            height: "16px",
                            minWidth: "16px",
                            backgroundColor: isDarkMode ? "#9d97f5" : "#6860ee",
                          },
                        }}
                      >
                        <img
                          src={searchFilterIcon}
                          alt="Filter"
                          style={{ width: "18px", height: "18px" }}
                        />
                      </Badge>
                      Filters
                    </span>
                  }
                  event={toggleAdvancedSearch}
                  className={`job-advanced-filter-btn ${
                    showAdvancedSearch || isAdvancedFilterPopupOpen
                      ? "active"
                      : ""
                  }`}
                  style={{
                    backgroundColor: isDarkMode
                      ? "rgba(157, 151, 245, 0.15)"
                      : "#f0efff",
                    color: isDarkMode ? "#9d97f5" : "#6860ee",
                    borderRadius: "10px",
                    padding: "12px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                    border: "none",
                    boxShadow: "none",
                    marginLeft: "8px",
                  }}
                />
              </div>
              {showAdvancedSearch &&
                !advancedFiltersInSidePopup &&
                renderAdvancedSearchFields()}
            </div>
          </div>
        </>
      )}
      {advancedFiltersInSidePopup &&
        createPortal(
          <SidePopup
            isOpen={isAdvancedFilterPopupOpen}
            onClose={() => setIsAdvancedFilterPopupOpen(false)}
            title="Filters"
            subTitle={publicSearch
              ? "Filter jobs by role, type, location, skills, priority, and experience range"
              : "Filter jobs by role, type, location, skills, priority, experience, and client"}
            sidePopupWidth="620px"
            contentPadding="18px"
          >
            {renderAdvancedSearchFields(true)}
            <div className="job-filter-sidepopup-actions">
              <Button
                label="Clear"
                event={handleClearSearch}
                isCancel={true}
                backgroundColor="#6860ee"
              />
              <Button
                label="Apply Filters"
                event={handleAdvancedFilterSubmit}
                backgroundColor="#6860ee"
                color="#ffffff"
                isLoading={isSimpleSearchLoading}
                disabled={isSimpleSearchLoading}
              />
            </div>
          </SidePopup>,
          document.body,
        )}
    </div>
  );
};

export default JobSimpleSearchBox;
